import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useFirebase } from '#features/api/utils/firebase';
import { getMeOptions } from '#modules/user/model/me';
import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';
import { addPushToken } from '../model/notifications';
import { waitForServiceWorker } from './waitForServiceWorker';

/**
 * Reconciles this browser's FCM token with the backend once per load.
 *
 * The web flow only ever sent the token on an explicit "enable" click, so any drift stayed
 * forever: FCM rotates a token on its own (SW reinstall, cleared site data, server-side
 * refresh), and — more often — the token belongs to the browser rather than to the account, so
 * signing in as somebody else leaves the row attached to the previous user. Both cases end with
 * a live subscription the backend doesn't know about: no notifications, and a "this browser is
 * not registered" banner that clicking the button could not fix.
 *
 * Runs only when permission is already granted, so it never triggers a prompt. Mount once, high
 * in the tree; native has its own listener-based sync (useNativePushTokenSync).
 */
/** Пауза перед единственным повтором: воркеру хватает, человеку незаметно. */
const RETRY_DELAY_MS = 2_000;

export const useWebPushTokenSync = () => {
  const isAuth = useIsAuth();
  const firebaseClient = useFirebase();
  const queryClient = useQueryClient();
  const { data: me } = useQuery({ ...getMeOptions(), enabled: isAuth });
  const serverTokens =
    me?.__typename === 'CurrentUser'
      ? (me.pushToken ?? [])
          .map((entry) => entry?.token)
          .filter((token): token is string => typeof token === 'string' && token.length > 0)
      : null;
  // One reconciliation per token value: without this the effect would re-send on every profile
  // refetch while the backend keeps answering with a list that doesn't contain our token.
  const syncedRef = useRef<string | null>(null);
  /** Один повтор на монтирование: больше - это уже опрос, а он тут ни к чему. */
  const retriedRef = useRef(false);

  useEffect(() => {
    if (envConfig.IS_NATIVE || !isAuth || !firebaseClient || serverTokens === null) {
      return;
    }

    if (typeof window === 'undefined' || !('Notification' in window)) {
      return;
    }

    if (Notification.permission !== 'granted') {
      return;
    }

    let active = true;

    void (async () => {
      /*
        🔴 Ждём живого воркера ПЕРЕД тем, как спросить токен. Без этого сверка проигрывала гонку
        всегда: воркер регистрируется по событию `load`, а хук смонтирован на гидрации, то есть
        раньше. На ещё устанавливающемся воркере обёртка возвращает null, отказ проглатывается -
        и человек с выданным разрешением оставался без токена на сервере.

        Ожидание стоит ПОСЛЕ проверки разрешения (выше): иначе оно висело бы на каждой загрузке
        у каждого, кто уведомления не разрешал.
      */
      await waitForServiceWorker();
      if (!active) {
        return;
      }

      let token: string | null = null;
      try {
        token = await firebaseClient.getPushToken();
      } catch (error) {
        console.warn('[push] token sync: getPushToken threw', error);
      }

      /*
        Токена нет - это не ответ, а неудача, и повторить её некому: ни одна из зависимостей
        эффекта от этого не меняется, так что до перезагрузки страницы вопрос закрыт. Поэтому
        один повтор делаем сами - воркер мог доехать за эти секунды.
      */
      if (active && !token && !retriedRef.current) {
        retriedRef.current = true;
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        if (!active) {
          return;
        }
        try {
          token = await firebaseClient.getPushToken();
        } catch (error) {
          console.warn('[push] token sync: retry threw', error);
        }
      }

      if (!active || !token || syncedRef.current === token) {
        return;
      }

      if (serverTokens.includes(token)) {
        // Already known to the backend — keep the local copy in step with it.
        localStorage.setItem('fcm_token', token);
        syncedRef.current = token;
        return;
      }

      try {
        const data = await addPushToken({ token, platform: 'WEB' });
        if (data?.__typename === 'Operation') {
          localStorage.setItem('fcm_token', token);
          syncedRef.current = token;
          queryClient.invalidateQueries({ queryKey: getMeOptions().queryKey });
        }
      } catch (error) {
        console.warn('[push] token sync: addPushToken failed', error);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, firebaseClient, queryClient, serverTokens?.join('|')]);
};
